// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Link } from "react-router-dom"
import { useI18n, formatEurFromCents } from "@/i18n"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"
import { BUDGET_SETTINGS_PATH } from "@/lib/budgetMigration"

/**
 * Today, while the account still carries an UNCLASSIFIED pre-v2 amount
 * (`budgetModel === "legacy_hard_cap"`, redesign 2026-09-04, founder
 * finding 5): instead of a "no valid week fits under your maximum" failure
 * the user never chose, explain the transition and lead to the setup card
 * in Settings. Server-driven — once the account is v2 the normal goal line
 * / maximum messaging returns on its own.
 */
export function BudgetTransitionNotice({ amountCents }: { amountCents: number | null }) {
  const { t, locale } = useI18n()
  const amount = amountCents === null ? "—" : formatEurFromCents(amountCents, locale)
  return (
    <div
      role="status"
      data-testid="today-budget-transition"
      className="flex flex-col gap-2 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink"
    >
      <p className="flex items-center gap-2 font-semibold">
        <span>{t("views.budget.todayTransitionTitle", { amount })}</span>
        <InfoButton termId="maximum-checkout" />
      </p>
      <p className="text-ink-2">{t("views.budget.todayTransitionBody", { amount })}</p>
      <div>
        <Button asChild size="sm" className="min-h-11">
          <Link to={BUDGET_SETTINGS_PATH}>{t("views.budget.todayTransitionCta")}</Link>
        </Button>
      </div>
    </div>
  )
}
